import { useState } from "react";
import { FiAlertCircle } from "react-icons/fi";
import { useParams, useRouteContext } from "@tanstack/react-router";
import { type GridColumnProps } from "@progress/kendo-react-grid";
import Card from "@/components/shared/Card";
import CategoryFilterCell from "@/components/shared/tables/CategoryFilterCell";
import KendoTableClientState from "@/components/shared/tables/KendoTableClientState";
import { stripAriaSelected } from "@/components/shared/tables/KendoTableClientState/kendo-cell-utils";
import ValidationFilterBar, {
  FilterType,
} from "../Validations/ValidationsFilterBar";
import ValidationsEditDialogs from "../Validations/ValidationsEditDialogs";
import { recordTypes } from "@/models";
import { useDiscrepanciesPage } from "@/components/discrepancies/data-discrepancies/hooks/useDiscrepanciesPage";
import { getRecordIdLabel } from "@/components/discrepancies/data-discrepancies/hooks/useDiscrepancyPopup";
import { useCSPPIDMatches, useLeaData } from "@/queries";
import { MatchLEAStatus } from "@/models/mid";

const SORTED_RECORD_TYPES = [...recordTypes].sort();

const MID_STATUS_LABELS: Record<number, string> = {
  [MatchLEAStatus.UNRESOLVED_ACTIVE_LEA]: "Unresolved",
  [MatchLEAStatus.UNRESOLVED_INACTIVE_LEA]: "Unresolved (Inactive)",
  [MatchLEAStatus.RESOLVED_MERGED]: "Merged",
  [MatchLEAStatus.RESOLVED_REJECTED]: "Not a Match",
  [MatchLEAStatus.RESOLVED_NO_LONGER_USED]: "No Longer Used",
};

const MID_ERROR_MESSAGE = "Review potential duplicate CSPPID";

function getErrorCodePillStyle(
  code: string,
  fatalErrorCodes: string[],
  warningCodes: string[]
): { bg: string; color: string } {
  if (fatalErrorCodes.includes(code)) {
    return { bg: "#fee2e2", color: "#b91c1c" };
  }
  if (warningCodes.includes(code)) {
    return { bg: "#fef9c3", color: "#a16207" };
  }
  return { bg: "#f1f5f9", color: "#475569" };
}

function buildColumns(
  fatalErrorCodes: string[],
  warningCodes: string[]
): GridColumnProps[] {
  return [
    {
      id: "RecordID",
      field: "RecordID",
      title: "Record ID",
      cells: {
        data: (props) => (
          <td {...stripAriaSelected(props.tdProps)}>
            {props.dataItem.RecordID}
          </td>
        ),
      },
    },
    {
      id: "ReferenceRecordType",
      field: "ReferenceRecordType",
      title: "Record Type",
      cells: {
        filterCell: (props) => CategoryFilterCell(props, SORTED_RECORD_TYPES),
      },
    },
    {
      id: "ErrorCode",
      field: "ErrorCode",
      title: "Error Code",
      cells: {
        data: (props) => {
          const code = props.dataItem.ErrorCode;
          const { bg, color } = getErrorCodePillStyle(
            code,
            fatalErrorCodes,
            warningCodes
          );
          return (
            <td {...stripAriaSelected(props.tdProps)}>
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  padding: "0.2rem 0.6rem",
                  borderRadius: "0.375rem",
                  backgroundColor: bg,
                  color: color,
                  fontSize: "0.8rem",
                  fontWeight: 600,
                  letterSpacing: "0.01em",
                }}
              >
                {code}
              </span>
            </td>
          );
        },
      },
    },
    { id: "ErrorMessage", field: "ErrorMessage", title: "Error Message" },
  ];
}

function buildSsidRows(discrepancies: ReturnType<typeof useDiscrepanciesPage>) {
  return discrepancies.discrepancies
    .filter((d) => d.ErrorCode?.startsWith("SSID"))
    .map((d) => ({
      RecordID: getRecordIdLabel(d),
      ReferenceRecordType: d.ReferenceRecordType,
      ErrorCode: d.ErrorCode,
      ErrorMessage:
        discrepancies.coreData.errorMessageMap.get(d.ErrorCode)
          ?.ErrorMessageText ?? "--",
      _original: d,
    }));
}

function buildMidRows(
  matchesData: ReturnType<typeof useCSPPIDMatches>["data"],
  leaData: ReturnType<typeof useLeaData>["data"]
) {
  if (!matchesData) return [];

  const allLeaCSPPIDs = new Set(Object.keys(leaData?.entities?.children ?? {}));

  return matchesData
    .filter((m) => m.Status === MatchLEAStatus.UNRESOLVED_ACTIVE_LEA)
    .map((matchLEA) => {
      const csppid1 = matchLEA.Match?.CSPPID ?? "";
      const csppid2 = matchLEA.Match?.MatchedCSPPID ?? "";
      const isInternal =
        allLeaCSPPIDs.has(csppid1) && allLeaCSPPIDs.has(csppid2);

      return {
        RecordID: csppid2 || "—",
        ReferenceRecordType: isInternal ? "Internal MID" : "External MID",
        ErrorCode:
          MID_STATUS_LABELS[matchLEA.Status] ?? String(matchLEA.Status),
        ErrorMessage: MID_ERROR_MESSAGE,
        _isMid: true as const,
        _matchLEA: matchLEA,
      };
    });
}

interface ValidationsTabProps {
  fatalErrors?: number;
  warnings?: number;
  ssidErrors?: number;
  mids?: number;
  discrepancies: ReturnType<typeof useDiscrepanciesPage>;
  canEditRecord: (type: string) => boolean;
}

export default function ValidationsTab({
  fatalErrors = 0,
  warnings = 0,
  ssidErrors = 0,
  mids = 0,
  discrepancies,
  canEditRecord,
}: ValidationsTabProps) {
  const [activeFilter, setActiveFilter] = useState<FilterType>("all");

  const leaContext = useRouteContext({ from: "/$leaid" });
  const params = useParams({ from: "/$leaid" });
  const leaid = params.leaid as string;

  const { data: matchesData } = useCSPPIDMatches(leaid);
  const { data: leaData } = useLeaData(leaContext.currentUserOrg.esiIdPath);

  const columns = buildColumns(
    discrepancies.fatalErrorCodes,
    discrepancies.warningCodes
  );

  const ssidRows = buildSsidRows(discrepancies);
  const midRows = buildMidRows(matchesData, leaData);

  const rows = discrepancies.tableData;
  let filteredTableData: any[] = [];
  if (activeFilter === "all") {
    const nonSsidRows = rows.filter(
      (row) => !row.ErrorCode?.startsWith("SSID")
    );
    filteredTableData = [...nonSsidRows, ...ssidRows, ...midRows];
  } else if (activeFilter === "fatal") {
    filteredTableData = rows.filter((row) =>
      discrepancies.fatalErrorCodes.includes(row.ErrorCode)
    );
  } else if (activeFilter === "warnings") {
    filteredTableData = rows.filter((row) =>
      discrepancies.warningCodes.includes(row.ErrorCode)
    );
  } else if (activeFilter === "ssid") {
    filteredTableData = ssidRows;
  } else if (activeFilter === "mids") {
    filteredTableData = midRows;
  }

  const handleRowClick = (event: any) => {
    if (event?.dataItem?._isMid) return;
    discrepancies.handleRowClick(event);
  };

  return (
    <div style={{ padding: "1.5rem 0" }}>
      <ValidationFilterBar
        activeFilter={activeFilter}
        onFilterChange={setActiveFilter}
        counts={{
          fatal: fatalErrors ?? 0,
          warnings: warnings ?? 0,
          ssid: ssidErrors ?? 0,
          mids: mids ?? 0,
        }}
      />

      <Card className="mb-3 no-padding card-border">
        <div
          className="text-white p-3 d-flex align-items-center gap-2"
          style={{
            backgroundColor: "#475569",
            fontWeight: 500,
            borderTopLeftRadius: "inherit",
            borderTopRightRadius: "inherit",
          }}
        >
          <FiAlertCircle size={18} />
          <span>Error Details</span>
        </div>
        <KendoTableClientState
          key={activeFilter}
          data={filteredTableData}
          columns={columns}
          error={discrepancies.error}
          onRowClick={handleRowClick}
        />
      </Card>

      <ValidationsEditDialogs
        discrepancies={discrepancies}
        canEditRecord={canEditRecord}
      />
    </div>
  );
}
